import { Branch } from '../../../dorian-shared/types/branch/Branch';
import { collectBranchEdges } from './branchLines';

/**
 * Builds an inbound index: maps each destination branchId → list of source branchIds
 * that have an edge pointing to it.
 *
 * Complexity: O(E) where E = total edges across all branches.
 * Build once, query many times via getLinksFrom(branchId, index).
 */
export function buildInboundIndex(branches: Branch[]): Map<number, number[]> {
  const index = new Map<number, number[]>();
  branches.forEach((branch, i) => {
    const edges = collectBranchEdges(branch, i);
    edges.forEach((edge) => {
      const existing = index.get(edge.toID);
      if (existing) {
        if (!existing.includes(edge.fromID)) {
          existing.push(edge.fromID);
        }
      } else {
        index.set(edge.toID, [edge.fromID]);
      }
    });
  });
  return index;
}

/**
 * Returns all outbound destination branchIds for the given branchId.
 * Deduplicates: a destination appears at most once even if multiple edge types point to it.
 *
 * O(N) per call (linear scan to find the branch). Acceptable for one-off lookups.
 * For repeated lookups consider building a Map<id, branch> once, analogous to buildInboundIndex.
 */
export function getLinksTo(branchId: number, branches: Branch[]): number[] {
  // branchIndex is used only for LineEdge.index (canvas rendering order); it does not affect fromID/toID.
  const idx = branches.findIndex((b) => Number(b.id) === branchId);
  if (idx === -1) return [];
  const branch = branches[idx];
  const edges = collectBranchEdges(branch, idx);
  const seen = new Set<number>();
  const result: number[] = [];
  edges.forEach((edge) => {
    if (!seen.has(edge.toID)) {
      seen.add(edge.toID);
      result.push(edge.toID);
    }
  });
  return result;
}

/**
 * Returns all inbound source branchIds for the given branchId.
 * Requires a pre-built inbound index from buildInboundIndex().
 */
export function getLinksFrom(branchId: number, inboundIndex: Map<number, number[]>): number[] {
  return inboundIndex.get(branchId) ?? [];
}
